import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { conversationDetailQueryKey } from "@/api/chat/chat";
import { shareConversationBatch } from "@/api/shared-conversations";
import type {
    ISharedConversationBatchRequest,
    ISharedConversationBatchResponse,
} from "@/api/shared-conversations";
import {
    sharedConversationQueryOptions,
    sharedConversationsQueryOptions,
} from "@/hooks/useSharedConversation";

/**
 * Share a Polly conversation with N colleagues in one batch call. Emits the
 * POST → invalidate cycle the rest of the app expects:
 *   - ["sharedConversations"] so each recipient's Shared-With-Me list
 *     refreshes on the next mount.
 *   - this conversation's share lookup, which is keyed outside that prefix.
 *   - the conversation's detail query so the owner's detail view picks up
 *     any backend-derived share-count badge without a reload.
 *
 * Partial success is NOT an error: the batch endpoint returns 200 with a
 * per-recipient `results` array (`succeeded` / `failed` rollups), so the
 * caller reads `data.results` to render the success-count toast plus inline
 * per-recipient errors. Only a transport failure (network / 5xx / malformed
 * body) lands in the mutation's `error` channel — hence `AxiosError`, with no
 * typed `ShareConversationError`: typed rejections now live inside the 200
 * body, one per recipient, not as a thrown class.
 */
export const useShareConversation = (conversationId: number | string) => {
    const queryClient = useQueryClient();

    return useMutation<
        ISharedConversationBatchResponse,
        AxiosError,
        ISharedConversationBatchRequest
    >({
        mutationFn: (body) => shareConversationBatch(conversationId, body),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: sharedConversationsQueryOptions.queryKey,
            });
            queryClient.invalidateQueries({
                queryKey: sharedConversationQueryOptions(String(conversationId))
                    .queryKey,
                exact: true,
            });
            queryClient.invalidateQueries({
                queryKey: conversationDetailQueryKey(String(conversationId)),
            });
        },
    });
};
